import type { ModeConfig } from "~/shared/data/chat-models";

export interface ReconcilableSettings {
    aspectRatio: string;
    resolution: number;
    duration: number;
    audioEnabled: boolean;
    characterOrientation: string | null;
    upscaleFactor: number | null;
    batchSize: number;
}

/** Правило согласования одной оси: возвращает свой кусок дельты либо пустой объект. */
type ReconcileRule = (mode: ModeConfig, values: ReconcilableSettings) => Partial<ReconcilableSettings>;

/** Широкоэкранное соотношение сторон предпочитаем любому другому, если режим его умеет. */
const PREFERRED_ASPECT_RATIO = "16:9";

// «Pull-up to backend defaults»: значение, выпавшее из набора режима, подтягиваем
// к default бэка (что админ выбрал дефолтом), а не к произвольному первому
// элементу. Вызывается только для непустого набора — голова списка тут последний
// рубеж на случай, когда дефолта у режима нет вовсе.
function pullUpToDefault<T>(options: readonly T[], backendDefault: T | null, preferred?: T): T {
    if (preferred !== undefined && options.includes(preferred)) return preferred;
    return backendDefault ?? options[0]!;
}

// Ось, которой у одного режима нет вовсе, а у другого она обязательна
// (character_orientation у Kling Motion Control, кратность у апскейла). Нет
// набора → значение сбрасываем, иначе уедет чужому режиму; есть набор →
// значение обязано в него входить, пустое тоже не подходит. `undefined` —
// «менять нечего».
function reconcileOptionalAxis<T>(
    options: readonly T[],
    current: T | null,
    backendDefault: T | null,
): T | null | undefined {
    if (options.length === 0) return current === null ? undefined : null;
    if (current !== null && options.includes(current)) return undefined;
    return pullUpToDefault(options, backendDefault);
}

const reconcileAspectRatio: ReconcileRule = (mode, values) => {
    const { aspectRatios } = mode;
    if (aspectRatios.length === 0 || aspectRatios.includes(values.aspectRatio)) return {};
    return { aspectRatio: pullUpToDefault(aspectRatios, mode.defaultAspectRatio, PREFERRED_ASPECT_RATIO) };
};

const reconcileResolution: ReconcileRule = (mode, values) => {
    const { resolutions } = mode;
    if (resolutions.length === 0 || resolutions.includes(values.resolution)) return {};
    return { resolution: pullUpToDefault(resolutions, mode.defaultResolution) };
};

const reconcileDuration: ReconcileRule = (mode, values) => {
    const { durations } = mode;
    // У режима без длительностей она не подтягивается, а обнуляется: длительности
    // такой генерации не существует, и чужую отправлять нельзя.
    if (durations.length === 0) return values.duration === 0 ? {} : { duration: 0 };
    if (durations.includes(values.duration)) return {};
    return { duration: pullUpToDefault(durations, mode.defaultDuration) };
};

/** Режим, диктующий состояние звука. Отсутствие ключа — выбор остаётся за пользователем. */
const AUDIO_FORCED_STATE: Partial<Record<ModeConfig["audioSupport"], boolean>> = {
    always: true,
    none: false,
};

const reconcileAudio: ReconcileRule = (mode, values) => {
    const forced = AUDIO_FORCED_STATE[mode.audioSupport];
    if (forced === undefined || forced === values.audioEnabled) return {};
    return { audioEnabled: forced };
};

const reconcileCharacterOrientation: ReconcileRule = (mode, values) => {
    const next = reconcileOptionalAxis(
        mode.characterOrientations,
        values.characterOrientation,
        mode.defaultCharacterOrientation,
    );
    return next === undefined ? {} : { characterOrientation: next };
};

const reconcileUpscaleFactor: ReconcileRule = (mode, values) => {
    const next = reconcileOptionalAxis(mode.upscaleFactors, values.upscaleFactor, mode.defaultUpscaleFactor);
    return next === undefined ? {} : { upscaleFactor: next };
};

// Размер пакета — ось со своим потолком: у новой модели пакета может не быть
// вовсе. Оставленный от прошлой модели выбор сделал бы запрос неисполнимым (бэк
// отказывает считать цену такого пакета), поэтому подрезаем до максимума
// режима, а не гасим кнопку молча.
const reconcileBatchSize: ReconcileRule = (mode, values) =>
    values.batchSize > mode.maxBatchSize ? { batchSize: mode.maxBatchSize } : {};

const RECONCILE_RULES: readonly ReconcileRule[] = [
    reconcileAspectRatio,
    reconcileResolution,
    reconcileDuration,
    reconcileAudio,
    reconcileCharacterOrientation,
    reconcileUpscaleFactor,
    reconcileBatchSize,
];

// Возвращает ТОЛЬКО изменившиеся поля (пустой объект, если всё валидно). НЕ
// трогает mode — его смена инвалидирует mode-зависимые наборы, поэтому reconcile
// делается на следующем тике после setMode.
export function computeSettingsReconciliation(
    currentMode: ModeConfig,
    values: ReconcilableSettings,
): Partial<ReconcilableSettings> {
    return RECONCILE_RULES.reduce<Partial<ReconcilableSettings>>(
        (delta, rule) => Object.assign(delta, rule(currentMode, values)),
        {},
    );
}
